import { FC, useEffect, useState } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { useNavigate, useParams } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Slider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { ControlledSelect, ControlledSelectOption, makeSxStyles } from "ui";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import { Delete, Close } from "@mui/icons-material";
import { useDialogStore } from "../../../state/dialog-store";
import {
  adminPutDriverReqDtoSchema,
  useDriverControllerDelete,
  useDriverControllerGetDriverById,
  useDriverControllerSendInvite,
  useDriverControllerUpdateById,
  useVehicleControllerGetUnassignedVehicles,
} from "service";
import {
  AdminPutDriverReqDto,
  AdminVehicleResDto,
} from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "../../../query-client";
import useAddressAutocomplete from "../../../hooks/use-autocomplete";
import {
  AppAccessEnum,
  appAccessEnumSelectOptions,
} from "service/types/enum/app-access-enum";
import HeaderItems from "../../../shared/components/header-items/HeaderItems";
import Navbar from "../../../components/navbar/navbar";
import {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import CredentialsBox from "../../../components/credentials-box/credentials-box";
import PasswordEdit from "../../../components/password-edit/password-edit";
import { useRegionManagement } from "../../../hooks/use-region-management";
import RegionAssignment from "../../../components/region-assignment/region-assignment";
import { z } from "zod";

// Custom validation schema that handles different driver types and password requirements
const createEditDriverValidationSchema = (hasUsername: boolean) => {
  const baseSchema = adminPutDriverReqDtoSchema
    .omit({
      email: true,
      password: true,
    })
    .extend({
      name: z.string().min(1, "Name is required"),
      startingPoint: z.string().min(1, "Starting point is required"),
    });

  const passwordSchema = z
    .string()
    .refine(
      (val) => {
        // If empty, it's valid (optional)
        if (!val || val.length === 0) return true;
        // If not empty and has 8+ characters, must contain letters and numbers
        return /^(?=.*[A-Za-z])(?=.*\d)/.test(val);
      },
      {
        message: "Password must contain letters and numbers",
      },
    )
    .refine(
      (val) => {
        // If empty, it's valid (optional)
        if (!val || val.length === 0) return true;
        // If not empty, must be at least 8 characters
        return val.length >= 8;
      },
      {
        message: "Password must be 8+ characters with letters and numbers",
      },
    );

  if (hasUsername) {
    // For drivers with username (manual registration)
    return baseSchema.extend({
      password: passwordSchema,
    });
  } else {
    // For drivers with email (email registration)
    return baseSchema.extend({
      email: z.string().email("Valid email is required"),
      password: passwordSchema,
    });
  }
};

const DriverDetailsPage: FC = () => {
  const styles = useStyles();
  const theme = useTheme();
  const { palette } = theme;
  const navigate = useNavigate();
  const [select, setSelect] = useState<ControlledSelectOption[]>([]);
  const [currentLicense, setCurrentLicense] = useState<
    ControlledSelectOption[]
  >([]);

  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [originalEmail, setOriginalEmail] = useState<string>("");
  const [originalFormValues, setOriginalFormValues] = useState<any>(null);
  const { setDialog } = useDialogStore();
  const { getPlacePredictions, placePredictions } = useAddressAutocomplete();

  const { driverId } = useParams<{
    driverId: string;
  }>();

  const { data: unassignedVehicles } =
    useVehicleControllerGetUnassignedVehicles({});

  const { data: driverData, isLoading } = useDriverControllerGetDriverById({
    pathParams: {
      driverId: driverId || "",
    },
  });

  const { regionOptions, checkConflictsAndProceed } = useRegionManagement({
    currentDriverId: driverId,
    originalRegionIds:
      driverData?.data?.suggestedRegions?.map((region) => region.regionId) ||
      [],
    onConfirm: () => submitForm(),
  });

  const { mutate } = useDriverControllerUpdateById({
    onSuccess: (_, variables) => {
      // Check if email was changed
      const emailWasChanged =
        variables.body.email &&
        originalEmail &&
        variables.body.email !== originalEmail;

      const successMessage = emailWasChanged
        ? "Driver updated. Invitation sent to the new email."
        : "Driver Updated Successfully";

      toast.success(successMessage);
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/drivers",
          operationId: "driverControllerGetAll",
          variables: {},
        }),
      );

      // Invalidate unassigned vehicles query since vehicle assignments might have changed
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicles/unassigned",
          operationId: "vehicleControllerGetUnassignedVehicles",
          variables: {},
        }),
      );
      resetForm();

      // Reset email warning state
      setShowEmailWarning(false);

      // Check if password was updated
      const passwordWasUpdated =
        variables.body.password && variables.body.password.length > 0;

      if (passwordWasUpdated && driverData?.data?.username) {
        // Navigate with credentials dialog for password update
        setTimeout(() => {
          navigate(AdRoutes.driverManagement, {
            state: {
              showCredentialsDialog: true,
              credentials: {
                username: driverData.data?.username || "",
                password: variables.body.password,
              },
              isPasswordUpdate: true,
            },
          });
        }, 10);
      } else {
        // Normal navigation
        setTimeout(() => {
          navigate(AdRoutes.driverManagement);
        }, 10);
      }
    },
  });

  const { mutate: removeDriver } = useDriverControllerDelete({
    onSuccess: () => {
      toast.success("Driver Deleted Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/drivers",
          operationId: "driverControllerGetAll",
          variables: {},
        }),
      );

      // Invalidate unassigned vehicles query since the deleted driver's vehicle is now unassigned
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicles/unassigned",
          operationId: "vehicleControllerGetUnassignedVehicles",
          variables: {},
        }),
      );
      setTimeout(() => {
        navigate(AdRoutes.driverManagement);
      }, 10);
    },
  });

  const { mutate: sendEmail, isLoading: isLoadingSendEmail } =
    useDriverControllerSendInvite({
      onSuccess: () => {
        toast.success("Email Sent Successfully");
      },
    });

  // Create validation schema based on driver registration type

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    resetForm,
    setFieldTouched,
    setFieldValue,
    validateForm,
  } = useFormik<
    AdminPutDriverReqDto & {
      licensePlate: string;
      password: string;
      regionIds: string[];
    }
  >({
    initialValues: {
      name: "",
      email: "",
      password: "",
      startingPoint: "",
      licensePlate: "",
      appAccess: false,
      available: false,
      vehicleId: null,
      performanceTime: 0,
      regionIds: [""],
    },
    validate: (
      values: AdminPutDriverReqDto & {
        licensePlate: string;
        password: string;
        regionIds: string[];
      },
    ) => {
      // Don't validate until driver data is loaded
      if (!driverData?.data) {
        return {};
      }

      try {
        // Dynamically create schema based on current driver data
        const currentHasUsername = !!driverData?.data?.username;
        const validationSchema =
          createEditDriverValidationSchema(currentHasUsername);
        validationSchema.parse(values);
        return {};
      } catch (error) {
        if (error instanceof z.ZodError) {
          const formikErrors: Record<string, string> = {};
          error.errors.forEach((err) => {
            if (err.path.length > 0) {
              formikErrors[err.path[0] as string] = err.message;
            }
          });
          return formikErrors;
        }
        return {};
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      const updateBody: Partial<AdminPutDriverReqDto> = {
        name: values.name,
        vehicleId:
          values.licensePlate === "noVehicle" ? null : values.vehicleId,
        available: values.available,
        performanceTime: values.performanceTime,
        startingPoint: values.startingPoint,
        appAccess: values.appAccess,
        regionIds: values.regionIds.filter((id) => id !== ""),
      };

      // Only include password if it's been changed
      if (values.password && values.password.length > 0) {
        updateBody.password = values.password;
      }

      // Include email only if driver doesn't have username
      if (!driverData?.data?.username) {
        updateBody.email = values.email
          ? values.email.toLowerCase()
          : undefined;
      }

      mutate({
        body: updateBody as AdminPutDriverReqDto,
        pathParams: {
          driverId: driverId || "",
        },
      });
    },
  });

  // Custom dirty state that ignores email casing changes
  // TODO: Refactor this in the future
  const isFormDirty = () => {
    if (!originalFormValues) return dirty;

    const currentValues = {
      name: values.name,
      email: values.email?.toLowerCase() || "",
      password: values.password,
      available: values.available,
      vehicleId: values.vehicleId,
      licensePlate: values.licensePlate,
      startingPoint: values.startingPoint,
      performanceTime: values.performanceTime,
      appAccess: values.appAccess,
      regionIds: values.regionIds,
    };

    const originalValues = {
      name: originalFormValues.name,
      email: originalFormValues.email?.toLowerCase() || "",
      password: originalFormValues.password,
      available: originalFormValues.available,
      vehicleId: originalFormValues.vehicleId,
      licensePlate: originalFormValues.licensePlate,
      startingPoint: originalFormValues.startingPoint,
      performanceTime: originalFormValues.performanceTime,
      appAccess: originalFormValues.appAccess,
      regionIds: originalFormValues.regionIds,
    };

    return JSON.stringify(currentValues) !== JSON.stringify(originalValues);
  };

  useUnsavedChangesBlocker(isFormDirty());

  const marks = [
    {
      value: -100,
      label: "-100%",
    },
    {
      value: 0,
      label: "0%",
    },
    {
      value: 100,
      label: "100%",
    },
  ];

  function valuetext(value: number) {
    return `${value}`;
  }

  useEffect(() => {
    if (!isLoading && driverData?.data) {
      const driverEmail = driverData.data.email || "";
      // Store original email in lowercase for consistent comparison
      setOriginalEmail(driverEmail.toLowerCase());

      const formValues = {
        name: driverData.data.name,
        email: driverEmail,
        password: "",
        available: Boolean(
          driverData.data.vehicle?.available && driverData.data.available,
        ),
        vehicleId: driverData.data.vehicleId || null,
        licensePlate: driverData.data.vehicle?.licensePlate || "noVehicle",
        startingPoint: driverData.data.startingPoint?.address,
        performanceTime: driverData.data.performanceTime,
        appAccess: driverData.data.appAccess,
        regionIds: driverData.data.suggestedRegions?.map(
          (region) => region.regionId,
        ) || [""],
      };

      resetForm({ values: formValues });
      setOriginalFormValues(formValues);

      // Reset email warning when form is populated with original data
      setShowEmailWarning(false);

      // Trigger validation after form is populated with driver data
      setTimeout(() => validateForm(), 0);

      if (driverData.data.vehicle) {
        setCurrentLicense([
          {
            label: driverData.data.vehicle.licensePlate,
            value: driverData.data.vehicle.licensePlate,
            disabled: true,
          },
        ]);
      }
    }
  }, [driverData, isLoading, validateForm]);

  useEffect(() => {
    if (unassignedVehicles && unassignedVehicles?.data) {
      const unassignedVehiclesOptions = unassignedVehicles?.data.reduce(
        (acc: ControlledSelectOption[], vehicle: AdminVehicleResDto) => {
          if (vehicle && vehicle.available) {
            acc.push({
              label: vehicle.licensePlate,
              value: vehicle.id,
            });
          }
          return acc;
        },
        [
          {
            label: "No Vehicle",
            value: "noVehicle",
          },
        ] as ControlledSelectOption[],
      );
      if (currentLicense?.length) {
        setSelect([...currentLicense, ...unassignedVehiclesOptions]);
      } else {
        setSelect([...unassignedVehiclesOptions]);
      }
    }
  }, [unassignedVehicles, currentLicense]);

  const filterSelectedVehicle = (value: string) => {
    if (value === "0") {
      setFieldValue("vehicleId", null);
      return;
    }
    const selVehicle = (unassignedVehicles?.data || []).filter(
      (item) => item.id === value,
    );
    if (selVehicle.length > 0) {
      setFieldValue("vehicleId", selVehicle[0].id);
    }
  };

  const onDeleteDriver = () => {
    setDialog({
      title: "Delete Driver",
      description: `Are you sure you want to delete driver ${values.name}? This will unassign all linked regions and cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        removeDriver({ pathParams: { driverId: driverId || "" } });
        onClose();
      },
    });
  };

  const onUpdateDriver = () => {
    checkConflictsAndProceed(values.regionIds);
  };

  const handleRegionChange = (newRegionIds: string[]) => {
    setFieldValue("regionIds", newRegionIds);
    setFieldTouched("regionIds", true);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = event.target.value;
    handleChange(event);

    // Show warning if email has changed from original (case-insensitive) and is not empty
    const emailChanged =
      originalEmail &&
      newEmail.trim() !== "" &&
      newEmail.trim().toLowerCase() !== originalEmail.trim().toLowerCase();

    setShowEmailWarning(!!emailChanged);
  };

  const handleCloseEmailWarning = () => {
    setShowEmailWarning(false);
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Driver Management",
                url: AdRoutes.driverManagement,
              },
              {
                label: "Edit Driver",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={!isFormDirty() || !isValid}
              variant="contained"
              onClick={onUpdateDriver}
              data-testid="save-changes"
            >
              Save Changes
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box sx={styles.driverDetailContainer}>
        <Typography>Name</Typography>
        <TextField
          id="name"
          type="text"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.name && errors.name}
          error={touched.name && !!errors.name}
          margin="dense"
          variant="outlined"
          fullWidth
        />

        <Typography>Starting Point</Typography>
        <Autocomplete
          freeSolo
          id="startingPoint"
          value={values.startingPoint}
          filterOptions={(x) => x}
          options={placePredictions.map((prediction) => {
            return { label: prediction.description, value: prediction };
          })}
          onChange={(_evt, value) => {
            if (value && typeof value === "object") {
              setFieldValue("startingPoint", String(value.label));
              setFieldTouched("startingPoint");
            }
          }}
          onBlur={() => setFieldTouched("startingPoint")}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(evt) => {
                getPlacePredictions({ input: evt.target.value?.trim() });
              }}
            />
          )}
        />
        <Typography>License Plate</Typography>
        <Box sx={{ width: "100%" }}>
          <ControlledSelect
            placeholderSelectEnabled={false}
            placeholderSelectLabel="Select License Plate"
            placeholder="License Plate"
            options={select}
            value={values.licensePlate as undefined | string}
            disabled={!driverData?.data?.driverCompletedRegistration}
            onChange={(value) => {
              setFieldValue("licensePlate", value);
              setFieldTouched("licensePlate", true);
              filterSelectedVehicle(value);
              setFieldValue("available", value === "noVehicle" ? false : true);
            }}
            data-testid="license-plate"
          />
          {!driverData?.data?.driverCompletedRegistration && (
            <Typography color="error" marginTop="10px">
              Unverified email addresses cannot be assigned a license plate.
            </Typography>
          )}
        </Box>

        <RegionAssignment
          regionIds={values.regionIds}
          regionOptions={regionOptions}
          onRegionChange={handleRegionChange}
          disabled={
            !driverData?.data?.driverCompletedRegistration || !values.available
          }
          disabledReason={
            !driverData?.data?.driverCompletedRegistration
              ? "Driver email address not verified"
              : !values.available
                ? "Driver is not available"
                : undefined
          }
        />
        <Typography>Availability</Typography>
        <Checkbox
          id="available"
          checked={values.available && values.licensePlate !== ""}
          sx={{ justifySelf: "flex-start" }}
          onChange={handleChange}
        />
        <Typography>Performance Time</Typography>
        <Slider
          id="performanceTime"
          marks={marks}
          valueLabelDisplay="on"
          getAriaValueText={valuetext}
          onChange={(_, val) => {
            setFieldValue("performanceTime", val);
            setFieldTouched("performanceTime", true);
          }}
          sx={styles.customSliderStyle}
          value={values.performanceTime}
          min={-100}
          max={100}
          defaultValue={0}
        />
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.driverDetailContainer}>
        {!driverData?.data?.username && (
          <>
            <Typography>Email</Typography>
            <Box sx={{ width: "100%" }}>
              <TextField
                id="email"
                type="text"
                value={values.email}
                onChange={handleEmailChange}
                onBlur={handleBlur}
                helperText={touched.email && errors.email}
                error={touched.email && !!errors.email}
                margin="dense"
                variant="outlined"
                fullWidth
              />

              {showEmailWarning && (
                <Box sx={styles.invalidSelection}>
                  <Box>
                    <Typography
                      color="#690005"
                      fontWeight={600}
                      data-testid="email-warning-title"
                    >
                      Updating the driver's email address revokes the drivers'
                      app access.
                    </Typography>
                    <Typography
                      color="#690005"
                      fontWeight={400}
                      data-testid="email-warning-body"
                    >
                      They will be unassigned from all vehicles, routes and
                      regions. We'll send a new invite to the driver's updated
                      address.
                    </Typography>
                  </Box>
                  <IconButton
                    aria-label="close"
                    size="small"
                    onClick={handleCloseEmailWarning}
                  >
                    <Close fontSize="inherit" sx={{ color: "#000" }} />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Typography fontWeight={600}>Verified Email</Typography>
            <Typography
              sx={{
                color: driverData?.data?.driverCompletedRegistration
                  ? palette.success["main"]
                  : palette.error["light"],
              }}
            >
              {driverData?.data?.driverCompletedRegistration
                ? "Verified"
                : "Not Verified"}
            </Typography>
          </>
        )}
        <Typography fontWeight={600}>Service Access</Typography>
        <ControlledSelect
          placeholderSelectEnabled={false}
          placeholdeShow={false}
          placeholder="Service Access"
          value={
            values.appAccess ? AppAccessEnum.active : AppAccessEnum.disabled
          }
          options={appAccessEnumSelectOptions}
          onChange={(value) => {
            setFieldValue("appAccess", value === AppAccessEnum.active);
            setFieldTouched("appAccess", true);
          }}
        />
        {!driverData?.data?.username && (
          <>
            <Typography fontWeight={600}>Invitation Email</Typography>
            <Button
              variant="outlined"
              sx={{ justifySelf: "flex-start" }}
              disabled={
                isLoadingSendEmail ||
                driverData?.data?.driverCompletedRegistration
              }
              onClick={() => {
                sendEmail({ pathParams: { driverId: driverId || "" } });
              }}
            >
              Send
            </Button>
          </>
        )}
        {driverData?.data?.username && (
          <>
            <Typography fontWeight={600}>Credentials</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                alignItems: "flex-start",
              }}
            >
              <CredentialsBox
                username={driverData?.data?.username || ""}
                showCopyButton={true}
              />
              <PasswordEdit
                onPasswordChange={(newPassword) => {
                  setFieldValue("password", newPassword);
                  setFieldTouched("password", true);
                  // Trigger validation manually to ensure form validation runs
                  setTimeout(() => validateForm(), 0);
                }}
                onCancel={() => {
                  setFieldValue("password", "");
                }}
                error={touched.password && !!errors.password}
                helperText={
                  touched.password && errors.password
                    ? errors.password
                    : undefined
                }
              />
            </Box>
          </>
        )}
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.driverDetailContainer}>
        <Typography fontWeight={600} alignSelf="start">
          Delete Driver
        </Typography>
        <Box display="grid" gap="20px" alignItems="flex-start">
          <Typography variant="caption">
            Before deleting this driver, please note that doing so will result
            in regions assigned to this driver in being unassigned.
          </Typography>
          <Button
            color="error"
            variant="outlined"
            sx={styles.deleteButton}
            onClick={onDeleteDriver}
            startIcon={<Delete />}
            data-testid="delete-driver-button"
          >
            Delete Driver
          </Button>
        </Box>
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  driverDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "center",
  },
  divider: {
    mt: "40px",
  },
  customSliderStyle: {
    "& .MuiSlider-valueLabel": {
      width: "28px",
      height: "34px",
      backgroundColor: "transparent",
      backgroundImage: "url('../../marker-indicator.svg')",
      backgroundPosition: "center center",
      backgroundSize: "cover",
      color: "#00344E",
    },

    "& .MuiSlider-track, & .MuiSlider-thumb": {
      color: "#8ACEFF",
    },

    "& .MuiSlider-markLabel": {
      color: "#C1C7CE",
    },
  },
  invalidSelection: {
    width: "100%",
    padding: "16px",
    backgroundColor: "#FFB4AB",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "16px",
  },
}));

export default DriverDetailsPage;
